// Relatórios → Transporte → Rotas do dia (GPS). Só o admin (fleet_tracking.read).
// Para cada rota iniciada no dia: km rodado × previsto, tempo em movimento e
// parado, paradas detectadas classificadas (prevista / depósito / não prevista),
// visitas às paradas da rota, trechos fora do corredor e alertas; consolidado
// por motorista, tempo médio por endereço e Excel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { AlertTriangle, ChevronDown, ChevronRight, Download, MapPinned, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { Badge, Card, CardContent, CardHeader } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { driverService } from '@services/driverService';
import { vehicleService } from '@services/vehicleService';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import type { Driver } from '@/types/driver';
import type { Vehicle } from '@/types/vehicle';
import type { AddressStopTime, FleetRoutesDailyReport, RouteDailyReport } from '../../types/vehicleTracker';
import { addDaysISO, formatClock, formatDayClock, formatDuration, formatKm, todayISO } from './fleetMapUtils';
import { STOP_KIND_INFO, SEVERITY_INFO, formatDateBRFromISO, formatDelta, kmDeltaPercent, routeSeverity } from './routeReportUtils';

const errorMessage = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);
const mapsLink = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`;

const FitRoute: React.FC<{ route: RouteDailyReport }> = ({ route }) => {
  const map = useMap();
  useEffect(() => {
    const coords: [number, number][] = [...route.planned_path, ...route.points.map((p) => [p.latitude, p.longitude] as [number, number])];
    if (coords.length > 1) map.fitBounds(L.latLngBounds(coords), { padding: [30, 30], maxZoom: 16 });
  }, [route, map]);
  return null;
};

const RouteMap: React.FC<{ route: RouteDailyReport }> = ({ route }) => (
  <div style={{ height: 380 }}>
    <MapContainer center={[-23.6, -46.7]} zoom={12} style={{ height: '100%', width: '100%', borderRadius: '0.5rem' }}>
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <FitRoute route={route} />
      {route.planned_path.length > 1 && <Polyline positions={route.planned_path} pathOptions={{ color: '#2563EB', weight: 2, dashArray: '6 6', opacity: 0.8 }} />}
      {route.points.length > 1 && (
        <Polyline positions={route.points.map((p) => [p.latitude, p.longitude] as [number, number])} pathOptions={{ color: '#7C3AED', weight: 4, opacity: 0.7 }} />
      )}
      {route.visits
        .filter((v) => v.latitude != null && v.longitude != null)
        .map((v) => (
          <CircleMarker
            key={`v-${v.driver_route_stop_id}`}
            center={[v.latitude!, v.longitude!]}
            radius={5}
            pathOptions={{ color: '#2563EB', weight: 2, fillColor: 'white', fillOpacity: 1 }}
          >
            <Popup>
              {v.seq}. {v.label}
              <br />
              {v.address}
            </Popup>
          </CircleMarker>
        ))}
      {route.stops.map((s) => (
        <CircleMarker
          key={`s-${s.start_at}-${s.latitude}`}
          center={[s.latitude, s.longitude]}
          radius={9}
          pathOptions={{ color: 'white', weight: 2, fillColor: STOP_KIND_INFO[s.kind].color, fillOpacity: 0.95 }}
        >
          <Popup>
            <strong>{STOP_KIND_INFO[s.kind].label}</strong>
            {s.planned_label ? ` — ${s.planned_seq ? `${s.planned_seq}. ` : ''}${s.planned_label}` : ''}
            <br />
            {formatClock(s.start_at)} {s.ongoing ? '(parado agora)' : s.end_at ? `a ${formatClock(s.end_at)}` : ''} · {formatDuration(s.duration_seconds)}
            {s.address && (
              <>
                <br />
                {s.address}
              </>
            )}
          </Popup>
        </CircleMarker>
      ))}
      {route.off_corridor.map((o) => (
        <CircleMarker key={`o-${o.start_at}`} center={[o.latitude, o.longitude]} radius={7} pathOptions={{ color: '#DC2626', weight: 3, fillOpacity: 0 }}>
          <Popup>
            Fora do corredor: {formatClock(o.start_at)} a {formatClock(o.end_at)} · até {(o.max_distance_m / 1000).toFixed(1)} km do caminho planejado
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  </div>
);

const RouteCard: React.FC<{ route: RouteDailyReport }> = ({ route }) => {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY_INFO[routeSeverity(route)];
  const delta = kmDeltaPercent(route.driven_km, route.planned_km);
  return (
    <Card>
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left p-4 flex flex-col md:flex-row md:items-center gap-2 hover:bg-slate-50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <span className="font-semibold">Rota #{route.driver_route_id}</span>
          <span className="text-slate-600 truncate">
            {route.driver_name} · {route.vehicle_label}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">
            {formatClock(route.started_at)} – {route.in_progress ? 'em andamento' : formatClock(route.finished_at)}
            {route.returned_at && ` · volta à base ${formatClock(route.returned_at)}`}
          </span>
          <Badge variant="neutral">
            {formatKm(route.driven_km)} / {formatKm(route.planned_km)} ({formatDelta(delta)})
          </Badge>
          {route.unplanned_stops_count > 0 && <Badge variant="danger">{route.unplanned_stops_count} fora da rota</Badge>}
          <Badge variant={sev.variant}>{sev.label}</Badge>
        </div>
      </button>
      {open && (
        <CardContent className="space-y-4 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-slate-500">Duração</div>
              <div className="font-medium">{formatDuration(route.duration_seconds)}</div>
            </div>
            <div>
              <div className="text-slate-500">Em movimento / parado</div>
              <div className="font-medium">
                {formatDuration(route.moving_seconds)} / {formatDuration(route.stopped_seconds)}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Parado nas paradas da rota</div>
              <div className="font-medium">{formatDuration(route.planned_seconds)}</div>
            </div>
            <div>
              <div className="text-slate-500">Parado fora da rota</div>
              <div className={`font-medium ${route.unplanned_seconds > 0 ? 'text-red-600' : ''}`}>{formatDuration(route.unplanned_seconds)}</div>
            </div>
          </div>

          {route.alerts.length > 0 && (
            <ul className="space-y-1 text-sm">
              {route.alerts.map((a) => (
                <li key={a} className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {a}
                </li>
              ))}
            </ul>
          )}

          <RouteMap route={route} />
          <p className="text-xs text-slate-500">
            Tracejado azul: caminho planejado em linha reta (depósito → paradas → depósito). Roxo: trajeto do GPS{route.points_sampled ? ' (simplificado)' : ''}.
            Bolas: verde parada prevista, azul depósito, vermelho não prevista. Círculo vermelho: ponto mais distante de um trecho a mais de 2 km do caminho
            planejado (aproximação: a rota real segue as ruas).
          </p>

          <div>
            <h4 className="font-medium mb-2">Paradas detectadas</h4>
            <div className="overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Início</th>
                    <th className="text-left px-3 py-2 font-medium">Fim</th>
                    <th className="text-left px-3 py-2 font-medium">Duração</th>
                    <th className="text-left px-3 py-2 font-medium">Tipo</th>
                    <th className="text-left px-3 py-2 font-medium">Onde</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {route.stops.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-center text-slate-500">
                        Nenhuma parada detectada no período da rota
                      </td>
                    </tr>
                  )}
                  {route.stops.map((s) => (
                    <tr key={`${s.start_at}-${s.latitude}`}>
                      <td className="px-3 py-1.5">{formatClock(s.start_at)}</td>
                      <td className="px-3 py-1.5">{s.ongoing ? <Badge variant="danger">parado agora</Badge> : formatClock(s.end_at)}</td>
                      <td className="px-3 py-1.5">{formatDuration(s.duration_seconds)}</td>
                      <td className="px-3 py-1.5">
                        <Badge variant={STOP_KIND_INFO[s.kind].variant}>{STOP_KIND_INFO[s.kind].label}</Badge>
                      </td>
                      <td className="px-3 py-1.5">
                        {s.kind === 'unplanned' ? (
                          <a href={mapsLink(s.latitude, s.longitude)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {s.address || `${s.latitude.toFixed(5)}, ${s.longitude.toFixed(5)}`}
                          </a>
                        ) : (
                          <>
                            {s.planned_seq ? `${s.planned_seq}. ` : ''}
                            {s.planned_label}
                            {s.distance_m != null && <span className="text-slate-400"> ({Math.round(s.distance_m)} m)</span>}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Paradas da rota</h4>
            <div className="overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">#</th>
                    <th className="text-left px-3 py-2 font-medium">Cliente / local</th>
                    <th className="text-left px-3 py-2 font-medium">Chegada (GPS)</th>
                    <th className="text-left px-3 py-2 font-medium">Tempo parado</th>
                    <th className="text-left px-3 py-2 font-medium">Entrega confirmada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {route.visits.map((v) => (
                    <tr key={v.driver_route_stop_id} className={v.is_delivered && v.has_coordinates && !v.visited && route.points.length > 0 ? 'bg-amber-50' : ''}>
                      <td className="px-3 py-1.5">{v.seq}</td>
                      <td className="px-3 py-1.5">
                        {v.label}
                        <div className="text-xs text-slate-500">{v.address}</div>
                      </td>
                      <td className="px-3 py-1.5">
                        {!v.has_coordinates ? <span className="text-slate-400">sem geolocalização</span> : v.visited ? formatClock(v.arrival_at) : '—'}
                      </td>
                      <td className="px-3 py-1.5">{v.stopped_seconds > 0 ? formatDuration(v.stopped_seconds) : v.visited ? 'só passou' : '—'}</td>
                      <td className="px-3 py-1.5">{v.is_delivered ? formatClock(v.delivered_at) : <span className="text-slate-400">pendente</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {route.off_corridor.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Fora do corredor (mais de 2 km do caminho planejado)</h4>
              <ul className="text-sm space-y-1">
                {route.off_corridor.map((o) => (
                  <li key={o.start_at}>
                    {formatClock(o.start_at)} a {formatClock(o.end_at)} · {formatDuration(o.duration_seconds)} · até {(o.max_distance_m / 1000).toFixed(1)} km ·{' '}
                    <a href={mapsLink(o.latitude, o.longitude)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      ver no mapa
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-sm">
            <Link to={`/driver-routes/${route.driver_route_id}`} className="text-blue-600 hover:underline">
              Abrir a rota
            </Link>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const AddressStopTimeSection: React.FC = () => {
  const today = todayISO();
  const [from, setFrom] = useState(addDaysISO(today, -29));
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState<AddressStopTime[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setRows(await vehicleTrackerService.stopTimeByAddress(from, to));
    } catch (err) {
      setError(errorMessage(err, 'Erro ao buscar o tempo por endereço'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Tempo médio parado por endereço de entrega" />
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <DateInputBR className="w-32 px-2 py-1.5 border border-slate-300 rounded-lg" value={from} onChange={(iso) => iso && setFrom(iso)} />
          <span className="text-slate-500">até</span>
          <DateInputBR className="w-32 px-2 py-1.5 border border-slate-300 rounded-lg" value={to} onChange={(iso) => iso && setTo(iso)} />
          <Button size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? 'Calculando…' : 'Calcular'}
          </Button>
          <span className="text-xs text-slate-500">Até 31 dias. Considera as paradas detectadas a até 150 m de uma parada da rota.</span>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {rows && (
          <div className="overflow-auto max-h-96 border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Cliente / local</th>
                  <th className="text-left px-3 py-2 font-medium">Endereço</th>
                  <th className="text-right px-3 py-2 font-medium">Visitas</th>
                  <th className="text-right px-3 py-2 font-medium">Tempo médio</th>
                  <th className="text-right px-3 py-2 font-medium">Tempo total</th>
                  <th className="text-left px-3 py-2 font-medium">Última</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-3 text-center text-slate-500">
                      Nenhuma parada em endereço de entrega no período
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={`${r.address}-${r.label}`}>
                    <td className="px-3 py-1.5">{r.label}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.address || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.visits}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{formatDuration(r.avg_seconds)}</td>
                    <td className="px-3 py-1.5 text-right">{formatDuration(r.total_seconds)}</td>
                    <td className="px-3 py-1.5">{formatDayClock(r.last_visit_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const FleetRoutesReportView: React.FC = () => {
  const today = todayISO();
  const [date, setDate] = useState(today);
  const [driverId, setDriverId] = useState<number | ''>('');
  const [vehicleId, setVehicleId] = useState<number | ''>('');
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [report, setReport] = useState<FleetRoutesDailyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    driverService.getAll({ page: 1, pageSize: 100 }).then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
    vehicleService.getAll({ page: 1, pageSize: 100 }).then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setReport(await vehicleTrackerService.routesDaily(date, driverId || undefined, vehicleId || undefined));
    } catch (err) {
      setError(errorMessage(err, 'Erro ao gerar o relatório'));
    } finally {
      setLoading(false);
    }
  }, [date, driverId, vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportExcel = async () => {
    try {
      setExporting(true);
      await vehicleTrackerService.downloadRoutesDailyExcel(date, driverId || undefined, vehicleId || undefined);
    } catch (err) {
      setError(errorMessage(err, 'Erro ao gerar o Excel'));
    } finally {
      setExporting(false);
    }
  };

  const totals = useMemo(() => {
    const rs = report?.routes ?? [];
    return {
      routes: rs.length,
      unplanned: rs.reduce((a, r) => a + r.unplanned_stops_count, 0),
      attention: rs.filter((r) => routeSeverity(r) === 'attention').length,
    };
  }, [report]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Dia</label>
          <div className="flex items-center gap-2">
            <DateInputBR className="w-36 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={date} onChange={(iso) => iso && setDate(iso)} />
            <Button variant={date === today ? 'primary' : 'secondary'} size="sm" onClick={() => setDate(today)}>
              Hoje
            </Button>
            <Button variant={date === addDaysISO(today, -1) ? 'primary' : 'secondary'} size="sm" onClick={() => setDate(addDaysISO(today, -1))}>
              Ontem
            </Button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Motorista</label>
          <select className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-[180px]" value={driverId} onChange={(e) => setDriverId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Todos</option>
            {drivers.map((d) => (
              <option key={d.driver_id} value={d.driver_id}>
                {d.full_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Veículo</label>
          <select className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-[180px]" value={vehicleId} onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Todos</option>
            {vehicles.map((v) => (
              <option key={v.vehicle_id} value={v.vehicle_id}>
                {v.license_plate} — {v.description}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 lg:ml-auto">
          <Button variant="secondary" onClick={() => void load()} disabled={loading} icon={<RefreshCw size={16} />}>
            Atualizar
          </Button>
          <Button variant="secondary" onClick={() => void exportExcel()} disabled={exporting || !report || report.routes.length === 0} icon={<Download size={16} />}>
            {exporting ? 'Gerando…' : 'Excel'}
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      {report && (
        <>
          <div className="flex flex-wrap gap-2 text-sm">
            <Badge variant="neutral">
              {totals.routes} rota(s) iniciada(s) em {formatDateBRFromISO(report.date)}
            </Badge>
            <Badge variant={totals.attention > 0 ? 'warning' : 'success'}>{totals.attention} com ocorrências</Badge>
            <Badge variant={totals.unplanned > 0 ? 'danger' : 'success'}>{totals.unplanned} parada(s) fora da rota</Badge>
          </div>

          {report.drivers.length > 0 && (
            <Card>
              <CardHeader title="Por motorista" />
              <CardContent className="p-0 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Motorista</th>
                      <th className="text-right px-3 py-2 font-medium">Rotas</th>
                      <th className="text-right px-3 py-2 font-medium">Km rodado / previsto</th>
                      <th className="text-right px-3 py-2 font-medium">Parado nas paradas</th>
                      <th className="text-right px-3 py-2 font-medium">Parado fora da rota</th>
                      <th className="text-right px-3 py-2 font-medium">Fora do corredor</th>
                      <th className="text-right px-3 py-2 font-medium">Alertas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.drivers.map((d) => (
                      <tr key={d.driver_id}>
                        <td className="px-3 py-2">{d.driver_name}</td>
                        <td className="px-3 py-2 text-right">{d.routes}</td>
                        <td className="px-3 py-2 text-right">
                          {formatKm(d.driven_km)} / {formatKm(d.planned_km)} ({formatDelta(kmDeltaPercent(d.driven_km, d.planned_km))})
                        </td>
                        <td className="px-3 py-2 text-right">{formatDuration(d.planned_seconds)}</td>
                        <td className={`px-3 py-2 text-right ${d.unplanned_seconds > 0 ? 'text-red-600 font-medium' : ''}`}>
                          {formatDuration(d.unplanned_seconds)} ({d.unplanned_stops_count})
                        </td>
                        <td className="px-3 py-2 text-right">{d.off_corridor_count}</td>
                        <td className="px-3 py-2 text-right">{d.alerts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {report.routes.length === 0 && !loading && (
            <Card>
              <CardContent className="p-6 text-center text-slate-500">
                <MapPinned className="mx-auto mb-2" />
                Nenhuma rota iniciada neste dia. O relatório usa o horário do "Iniciar Entrega"; rotas iniciadas antes de 12/10/2026 não têm esse horário.
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {report.routes.map((r) => (
              <RouteCard key={r.driver_route_id} route={r} />
            ))}
          </div>
        </>
      )}

      <AddressStopTimeSection />
    </div>
  );
};
